import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'

type CreateProposalConfig = {
  user?: api.AuthenticatedUser
}

export async function createProposal(
  payload: api.CreateProposal | api.CreateAnonymousProposal,
  { user }: CreateProposalConfig = {},
): Promise<api.ApiResponse<api.Proposal>> {
  const response =
    user === undefined
      ? await api.api.post.proposals({
          body: payload,
        })
      : await api.api.post.userProposals({
          urlParams: { userId: String(user.id) },
          body: payload,
          withToken: true,
        })

  return response.next((sucess) => sucess.body)
}

export type GetProposalConfig<EXTRA_FIELDS extends string = ''> = {
  withToken?: boolean
  extraFields?: EXTRA_FIELDS[]
}

export async function getProposal<EXTRA_FIELDS extends string = ''>(
  token: string,
  { withToken, extraFields = [] }: GetProposalConfig<EXTRA_FIELDS> = {},
): Promise<api.ApiResponse<api.Proposal<EXTRA_FIELDS>>> {
  const response = await api.api.get.proposal({
    urlParams: {
      token,
    },
    getParams: {
      extraFields: extraFields.join(','),
    },
    withToken,
  })

  return response.next((sucess) => sucess.body as api.Proposal<EXTRA_FIELDS>)
}

export type UpdateProposalConfig<EXTRA_FIELDS extends string = ''> = {
  withToken?: boolean
  extraFields?: EXTRA_FIELDS[]
}

export async function updateProposal<EXTRA_FIELDS extends string = ''>(
  token: string,
  proposal: api.UpdateProposal,
  { withToken, extraFields = [] }: UpdateProposalConfig<EXTRA_FIELDS> = {},
): Promise<api.ApiResponse<api.Proposal<EXTRA_FIELDS>>> {
  const response = await api.api.patch.proposal({
    urlParams: {
      token,
    },
    getParams: {
      extraFields: extraFields.join(','),
    },
    body: proposal,
    withToken,
  })

  return response.next((sucess) => sucess.body as api.Proposal<EXTRA_FIELDS>)
}

export async function refuseProposal(token: string) {
  const response = await api.api.post.proposalRefuse({
    urlParams: {
      token,
    },
    body: {
      answer: 'invalid-duplicate',
      answer_code: api.ProposalAnswerCode.INVALID_DUPLICATE,
    },
  })

  return response.next((sucess) => sucess.body)
}

const SHARED_PROPOSAL_CACHE = jotai.atom<hooks.Cache<api.Proposal>>({})

export function setInitialProposal(
  store: ReturnType<typeof jotai.createStore>,
  proposal: api.Proposal,
  user?: api.AuthenticatedUser<any>,
  extraFields: string[] = [],
): void {
  store.set(SHARED_PROPOSAL_CACHE, (cache) => {
    cache[JSON.stringify({ token: proposal.token, withToken: user !== undefined, extraFields })] = {
      data: proposal,
      status: hooks.QueryStatus.SUCCESS,
    }
    return cache
  })
}

export type UseProposal<EXTRA_FIELDS extends string = ''> = {
  response: hooks.Response<api.Proposal<EXTRA_FIELDS>>
  query: (config?: GetProposalConfig<EXTRA_FIELDS>) => Promise<void>
  mutate: (proposal: api.Proposal<EXTRA_FIELDS>) => void
  refuse: () => Promise<void>
  update: (
    patch: api.UpdateProposal,
    config?: UpdateProposalConfig<EXTRA_FIELDS>,
  ) => Promise<hooks.Response<api.Proposal<EXTRA_FIELDS>>>
}

export function useProposal<EXTRA_FIELDS extends string = ''>(
  token?: string,
  config?: GetProposalConfig<EXTRA_FIELDS>,
): UseProposal<EXTRA_FIELDS> {
  const sharedQuery = hooks.useSharedQuery(getProposal, {
    atom: SHARED_PROPOSAL_CACHE,
    id,
  })

  return { response: getResponse(), query, mutate, update, refuse }

  function id(token: string, config?: GetProposalConfig<EXTRA_FIELDS>): string {
    return JSON.stringify({ token, withToken: config?.withToken, extraFields: config?.extraFields })
  }

  async function query(): Promise<void> {
    if (token) {
      return sharedQuery.query(token, config)
    }
  }

  async function update(
    patch: Partial<api.Proposal>,
    config?: UpdateProposalConfig<EXTRA_FIELDS>,
  ): Promise<hooks.Response<api.Proposal<EXTRA_FIELDS>>> {
    if (token === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    // first, update the local cache
    sharedQuery.mutate([token, config], (response) => {
      const currentData = hooks.data(response)

      // if currently there is no data (because cache is empty, or there is no
      // data yet) the first mutation does not modify the cache
      if (currentData === undefined) {
        return response
      }

      // otherwise we update the data in cache with the patch that will be
      // sent to the api
      return { data: { ...currentData, ...patch }, status: hooks.QueryStatus.SUCCESS }
    })

    // then update on the api
    const response = await updateProposal(token, patch, config)

    // depending on the response, update the cache
    return response.caseOf<hooks.Response<api.Proposal<EXTRA_FIELDS>>>({
      left: (failure) => {
        sharedQuery.mutate([token, config], { failure, status: hooks.QueryStatus.FAILURE })
        return { failure, status: hooks.QueryStatus.FAILURE }
      },
      right: (success) => {
        sharedQuery.mutate([token, config], { data: success, status: hooks.QueryStatus.SUCCESS })
        return { data: success, status: hooks.QueryStatus.SUCCESS }
      },
    })
  }

  async function refuse(): Promise<void> {
    if (token === undefined) {
      return
    }

    const response = await refuseProposal(token)

    response.caseOf({
      left: async (failure) => {
        // if we get a 403, we tried to refuse a non anonymous proposal. This
        // error is expected, and should just be a noop
        if (failure.status === 403) {
          return
        }
        sharedQuery.mutate([token, config], { failure, status: hooks.QueryStatus.FAILURE })
      },
      right: async (success) => {
        sharedQuery.mutate([token, config], { data: success, status: hooks.QueryStatus.SUCCESS })
      },
    })
  }

  function mutate(proposal: api.Proposal<EXTRA_FIELDS>): void {
    if (token) {
      return sharedQuery.mutate([token], { data: proposal, status: hooks.QueryStatus.SUCCESS })
    }
  }

  function getResponse(): hooks.Response<api.Proposal<EXTRA_FIELDS>> {
    if (token === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    const response = sharedQuery.cache[id(token, config)]

    if (response === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }

    return response as hooks.Response<api.Proposal<EXTRA_FIELDS>>
  }
}
